<template>
	<div class="flex flex-col overflow-y-scroll">
		<AppPageHeader :back-link content-classes="p-[1.5rem]">
			<OptionInput
				v-model="selectedOfferId"
				:options="
					offers.map((offer) => ({
						label: getLoanOfferName(offer),
						value: offer.id,
					}))
				"
				placeholder="Kies..."
				class="text-sm"
			/>
		</AppPageHeader>

		<AppCardPage v-if="selectedOffer" class="w-full max-w-[1000px] mx-auto">
			<AppCard>
				<LoanOfferCard
					:offer="selectedOffer"
					:border="false"
					:title="getLoanOfferName(selectedOffer)"
				/>

				<div>
					<template v-if="descriptionLines?.length > 0">
						<p class="text-sm text-gray-500">Omschrijving:</p>
						<div>
							<p v-for="line in descriptionLines" :key="line">
								{{ line }}
							</p>
						</div>
					</template>

					<LoanApplicationKeyValue :entries class="pt-6 space-y-2" />
				</div>
			</AppCard>

			<AppCard>
				<section class="space-y-4">
					<div class="space-y-1">
						<h3 class="font-semibold">Ontvangen documenten</h3>
						<p class="text-gray-500 text-sm">
							De geldverstrekker heeft deze documenten bij
							{{
								selectedOffer.type === "preliminary"
									? "het renteaanbod"
									: "de offerte"
							}}
							gevoegd:
						</p>
					</div>

					<LoanOfferDocumentsGrid
						v-if="selectedOffer"
						:key="selectedOffer.id"
						class="pt-2"
						:application-id
						:offer-id="selectedOffer.id"
					/>
				</section>
			</AppCard>
		</AppCardPage>

		<section v-else class="flex items-center justify-center gap-1 min-h-64">
			<p class="text-gray-500">Geen offerte geselecteerd</p>
			<AppIcon name="sentiment_dissatisfied" class="text-gray-500" />
		</section>
	</div>
</template>

<script lang="ts" setup>
import type { ReadLoanOffer } from "@/generatedApi";
import { getLoanOfferName } from "@/helpers/hdnHelpers";
import { api } from "@/loanLinkApi";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

const backLink = {
	label: "Terug naar overzicht",
	to: { name: "LoanApplicationDetailsPage" },
};

const route = useRoute<"LoanOffersPage">();
const applicationId = route.params.loanApplicationId as string;

const offers = ref<ReadLoanOffer[]>([]);
const selectedOfferId = ref<string | undefined>();
const selectedOffer = computed(() =>
	offers.value.find((offer) => offer.id === selectedOfferId.value),
);

const descriptionLines = computed(() =>
	selectedOffer.value?.description
		? Object.entries(selectedOffer.value.description).map(([, value]) => value)
		: [],
);

watch(offers, () => {
	if (!selectedOfferId.value && offers.value.length > 0) {
		selectedOfferId.value = offers.value[offers.value.length - 1]?.id;
	}
});

async function getOffers() {
	await api.loanApplication
		.getOffers(applicationId)
		.then((res) => (offers.value = res.data ?? []));
}

function getBooleanTranslation(value?: boolean | null) {
	return value ? "Ja" : "Nee";
}

const entries = computed(() => ({
	"Document getekend retour:": getBooleanTranslation(
		selectedOffer.value?.mustReturnSignedCopy,
	),
	"Verlenging mogelijk:": getBooleanTranslation(
		selectedOffer.value?.extensionPossible,
	),
}));

onMounted(getOffers);
</script>
